<template>
  <v-app class="portal-layout">
    <v-layout
      v-if="profileLoading || !profile.id"
      class="portal-layout__spinner"
      row
      justify-center
      align-center
    >
      <spinner />
    </v-layout>

    <portal-sidebar />

    <template v-if="profile.id">
      <router-view :key="$route.fullPath" />
    </template>

    <portal-auth-modal v-model="authModal" />

    <template v-if="profile.id">
      <confirmation-modal />
      <portal-a-i-search />
      <file-viewer-dialog />
    </template>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import theme from '@/factories/theme';
import localStore from 'store';
import EventBus from '@/factories/eventBus';
import { ablyAction } from '@/lib/ably';
import {
  askNotificationsPermission,
  isNotificationsEnabled,
} from '@/lib/notification';
import { isMobileDevice } from '@/filters/helpers';
import isEmpty from 'lodash/isEmpty';

import PortalAuthModal from '../portalAuth/PortalAuthModal';
import ConfirmationModal from '@/components/basic/confirmation-modal/ConfirmationModal.vue';
import PortalAISearch from '@/views/portal/components/portalSearch/PortalAISearch.vue';
import PortalSidebar from '@/views/portal/components/layout/navigation/PortalSidebar.vue';
import FileViewerDialog from '@/views/files/common/FileViewerDialog.vue';

export default {
  name: 'PortalLayout',
  components: {
    PortalSidebar,
    ConfirmationModal,
    PortalAuthModal,
    PortalAISearch,
    FileViewerDialog,
  },
  data() {
    return {
      authModal: null,
    };
  },
  watch: {
    orgMeta: {
      immediate: true,
      deep: true,
      handler() {
        this.handleOrgMeta();
      },
    },

    orgStyles(newStyles) {
      if (newStyles) {
        this.updateTheme(newStyles);
      }
    },

    profile(val) {
      if (isEmpty(val)) return;

      // Dismiss any auth error modal — profile loaded means auth succeeded
      if (this.authModal) this.authModal = null;

      this.fetchResources();
    },
  },
  computed: {
    ...mapGetters([
      'profile',
      'profilePid',
      'profileLoading',
      'orgStyles',
      'orgMeta',
    ]),
  },
  methods: {
    ...mapActions([
      'loadProfile',
      'loginPortal',
      'refreshPortalThreads',
      'fetchUserRole',
    ]),
    ...mapMutations(['setProfilePid']),
    ...mapActions('portalSharedLessons', {
      loadSharedLessonFolders: 'loadFolders',
    }),
    ...mapActions('portalSharedFiles', {
      loadSharedFileFolders: 'loadFolders',
    }),

    setPid() {
      let params = this.$route.params;
      if (params && params.pid && !this.profilePid) {
        this.setProfilePid(params.pid);
      }
    },

    handleOrgMeta() {
      if (!this.orgMeta) return;
      const { portal_css, features } = this.orgMeta;

      if (portal_css) {
        this.addCustomCSS(portal_css);
      }

      if (features?.portal_messaging) {
        this.refreshPortalThreads();
        askNotificationsPermission();
      }
    },

    //the purpose of this method is to check to see if a theme is being dynamically
    //passed via the URL. We do this so that we can preview the portal for peoples brands

    checkSavedTheme(newTheme) {
      let key = 'portal-theme';

      //if writing with newTheme
      if (newTheme) {
        localStore.set(key, newTheme);
      } else {
        newTheme = localStore.get(key);
        if (newTheme) {
          theme.trigger('customThemeSet', newTheme);
        }
      }
    },

    async updateTheme(orgStyles) {
      const newTheme = await theme.setCustomTheme(orgStyles);
      this.checkSavedTheme(newTheme);
    },

    async login() {
      await this.$nextTick();

      let authData = { schema: 'full' };
      let query = this.$route.query;

      if (query?.code) {
        authData.okta = query.code;
        authData.workos = query.code;
      }

      try {
        await this.loginPortal(authData);
      } catch (err) {
        this.handleAuthError(err?.data);
      }
    },

    handleAuthError(data) {
      if (!data || data.status !== 'failed') return;

      const errorData = data.data || {};

      // Clear stored token when re-authentication is required
      // This happens when: token is invalid/expired, or org auth settings changed
      if (
        errorData.reauth_required ||
        this.shouldClearToken(errorData.error_type)
      ) {
        const testingPid = localStore.get('portal_testing_pid');
        if (testingPid) {
          localStore.remove(`auth_${testingPid}`);
          localStore.remove('portal_testing_pid');
        } else {
          localStore.remove('portal_auth');
        }
      }

      this.authModal = data;
    },

    shouldClearToken(errorType) {
      const clearTokenErrors = [
        'token_invalid',
        'token_expired',
        'reauth_required',
      ];
      return clearTokenErrors.includes(errorType);
    },

    addCustomCSS(path) {
      if (!path) return;

      let link = document.createElement('link');
      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.href = path;

      let headScript = document.querySelector('script');
      headScript.parentNode.insertBefore(link, headScript);
    },

    toggleVisibilityListener(add = false) {
      if (!isMobileDevice() && isNotificationsEnabled()) {
        if (add) return;
        return ablyAction('leave');
      }
      let method = (add ? 'add' : 'remove') + 'EventListener';
      window[method]('visibilitychange', this.checkVisibility, true);
    },

    checkVisibility() {
      if (!this.orgMeta?.features?.portal_messaging) return;

      const isVisible = document.visibilityState === 'visible';
      if (!isVisible) {
        ablyAction('leave');
      } else {
        ablyAction('enter');
        this.refreshPortalThreads();
      }
    },

    fetchResources() {
      this.loadSharedLessonFolders();
      this.loadSharedFileFolders();

      if (this.profile?.org_meta?.features?.portal_contacts) {
        this.fetchUserRole();
      }
    },
  },
  mounted() {
    this.toggleVisibilityListener(true);
  },
  created() {
    this.setPid();
    this.checkSavedTheme();
    this.login();

    // Handle auth errors from API calls (mid-session failures)
    EventBus.$on('authFailed', (errorData) => {
      setTimeout(() => {
        if (this.authModal) return;

        // If error data is provided, handle it directly
        if (errorData?.status === 'failed') {
          this.handleAuthError(errorData);
        } else {
          // Fallback: attempt re-login
          this.login();
        }
      }, 200);
    });
  },
  beforeDestroy() {
    this.toggleVisibilityListener();
  },
};
</script>

<style>
body {
  overflow: visible !important;
}
</style>

<style lang="less" src="@/assets/styles/portal/portal-layout.less"></style>
